/**
 * API Client - Axios instance with JWT interceptor.
 *
 * Security model:
 *   - Access token attached from in-memory Zustand store.
 *   - Refresh token sent automatically as httpOnly cookie (withCredentials).
 *   - On 401: POST /v1/auth/refresh (empty body, cookie sent automatically).
 *   - Proactive refresh scheduled via auth store (before token expires).
 */

import axios from 'axios';
import { useAuthStore } from '../stores/auth';

// Use relative path so Vite proxy works in dev.
// In production, either VITE_API_URL (the config in wrangler.toml /
// Cloudflare Pages dashboard) or VITE_API_BASE (the legacy name) must be
// set to the API host. We accept both so a config that uses either name
// works - previously only VITE_API_BASE was read while every config
// wrote VITE_API_URL, so the production build silently became
// origin-relative and the SPA's own index.html was served instead of
// the API responses.
import { resolveApiBase } from "./apiBase";

const API_BASE =
  resolveApiBase() ||
  '';

/**
 * Paywall detail carried on a 402. The backend sends this when a metered action
 * (AI generation, job match, ...) exceeds the plan's monthly allowance. It's
 * broadcast on the `resumark:paywall` window event for a global listener to
 * turn into an upgrade prompt. Inert while the free beta is on (no 402s fire).
 */
export interface PaywallDetail {
  code: string;        // "quota_exceeded"
  meter: string;       // "ai_generations" | "job_matches" | "data_sources"
  limit: number;
  used: number;
  plan: string;        // "free" | "scholar" | "pro"
  message: string;     // short, human, ready to show as-is
  upgrade_url: string; // where the Upgrade action should go, e.g. "/billing"
}

const api = axios.create({
  baseURL: API_BASE,
  // Render's free tier sleeps after ~15 min idle and can take "50 seconds or
  // more" to wake. 30s aborted mid-wake → spurious "Connect failed"/"Sync
  // failed". 60s lets a cold start finish in one request; the response
  // interceptor also retries once on transient cold-start errors.
  timeout: 60_000,
  headers: {
    'Content-Type': 'application/json',
  },
  // NOTE: withCredentials is NOT set globally.
  // We only enable credentials when needed for authenticated requests.
});

// ── Request interceptor: attach JWT + credentials ────────────

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Only enable credentials for authenticated requests or auth refresh endpoints.
  // This prevents mobile Safari CORS preflight issues on unauthenticated requests.
  const needsCredentials = 
    !!token || 
    config.url?.includes('/v1/auth/refresh') ||
    config.url?.includes('/v1/auth/logout');
  
  if (needsCredentials) {
    config.withCredentials = true;
  }
  
  return config;
});

// ── Response interceptor: handle 401 + refresh ───────────────

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token!);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(api(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // refresh_token cookie is sent automatically; the body carries only the
        // remember-me choice so the server matches the rotated cookie's lifetime
        // to it (session cookie when not remembered, remember-window sliding when it is).
        const { data } = await axios.post(
          `${API_BASE}/v1/auth/refresh`,
          { remember: useAuthStore.getState().rememberMe },
          { withCredentials: true },
        );

        // Use expires_in from response for accurate expiry tracking
        const expiresIn = data.expires_in || 900; // Default 15 min
        useAuthStore.getState().setAccessToken(data.access_token, expiresIn);
        processQueue(null, data.access_token);

        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // A REFUSAL AND A FAILURE TO ASK ARE DIFFERENT THINGS.
        //
        // Only 401/403 from /v1/auth/refresh means "this session is dead".
        // Everything else - a timeout, ERR_NETWORK, a 502 from a Render cold
        // start or a mid-deploy blip - means we could not reach the server, and
        // signing somebody out over it throws away a session that was never
        // rejected. This path used to log out and hard-redirect to "/" for any
        // failure at all, so a backend restart read to the user as being kicked
        // out mid-edit. The store's own refreshSession already draws this line;
        // drawing it here too is what makes the two agree.
        const refreshStatus =
          (refreshError as { response?: { status?: number } })?.response?.status;
        if (refreshStatus === 401 || refreshStatus === 403) {
          // Use async logout to properly revoke server token
          await useAuthStore.getState().logoutAsync();
          // Land on the marketing home, not the bare login form, so a deliberate
          // sign-out OR an expired session both return to "/" (where Sign in lives).
          window.location.href = '/';
        } else {
          console.warn('[API] Could not reach the server to refresh; keeping the session.');
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Paywall: a metered action exceeded the plan's monthly allowance. The
    // backend replies 402 with a { code:"quota_exceeded", ... } detail. Broadcast
    // it once so a global listener can show the upgrade prompt; the caller still
    // gets a rejected promise so its own spinner stops. (No 402s fire during the
    // free beta, so this path is dormant until charging is switched on.)
    if (error.response?.status === 402) {
      const detail = (error.response.data as { detail?: PaywallDetail } | undefined)?.detail;
      if (detail?.code === 'quota_exceeded' && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent<PaywallDetail>('resumark:paywall', { detail }));
      }
      return Promise.reject(error);
    }

    // Transient / cold-start resilience: a sleeping Render free-tier service,
    // while waking, can refuse the connection, time out, or return 502/503/504.
    // Retry ONCE after a short backoff (by then it's usually up) so a connect
    // or sync doesn't spuriously fail on the first action after idle. Excludes
    // the refresh endpoint (handled above) to avoid loops.
    const status = error.response?.status;
    const isTransient =
      error.code === 'ECONNABORTED' || // client timeout (cold start still waking)
      error.code === 'ERR_NETWORK' ||  // connection refused / CORS during boot
      status === 502 || status === 503 || status === 504;
    const url: string = originalRequest?.url || '';
    // Idempotent requests (GET / PUT / HEAD) - including the draft auto-save
    // (PUT) - retry a few times with growing backoff, because a cold start or a
    // mid-deploy blip can take tens of seconds (one 1.5s retry isn't enough).
    // POSTs keep a single retry to avoid any double-submit. __coldRetry counts.
    const method = String(originalRequest?.method || 'get').toLowerCase();
    const idempotent = method === 'get' || method === 'put' || method === 'head';
    const maxRetries = idempotent ? 3 : 1;
    const attempt: number = originalRequest?.__coldRetry || 0;
    if (
      isTransient &&
      originalRequest &&
      attempt < maxRetries &&
      !url.includes('/v1/auth/refresh')
    ) {
      originalRequest.__coldRetry = attempt + 1;
      const delay = [1500, 4000, 9000][attempt] ?? 9000;
      await new Promise((resolve) => setTimeout(resolve, delay));
      return api(originalRequest);
    }

    return Promise.reject(error);
  },
);

export default api;

/**
 * Defensive coercion for API responses that should always be arrays.
 *
 * Why: FastAPI returns `{detail: "Not Found"}` on 404 and similar
 * envelopes on auth errors. axios resolves those as 200-ish with the
 * envelope as `.data`, so `response.data || []` evaluates truthy and
 * the page ends up calling `.map` / `.find` / `.filter` on an object.
 *
 * Every page that does `setList(res.data)` should pipe through this.
 */
export function asList<T = unknown>(value: unknown, key?: string): T[] {
  if (Array.isArray(value)) return value as T[];
  if (key && value && typeof value === 'object' && Array.isArray((value as Record<string, unknown>)[key])) {
    return (value as Record<string, unknown>)[key] as T[];
  }
  return [];
}
